// LLM / 画像生成クライアント — OpenRouter API を利用者自身のキーで直接呼び出す（2026-07-07 全面改定・計画書§16.4）。
// - キーは利用者のブラウザ（API設定画面の localStorage）からリクエストヘッダで都度受領（メモリのみ・ファイル保存禁止。環境変数は CLI 都度指定用）
// - Workers AI はフォールバック含め全面禁止（provider 指定されても明示エラー）
// - コストは OpenRouter の usage.cost（実費 $）を毎回ログ表示。エラーは呼び出し元でダイアログ表示される前提の明確な文言にする
import { existsSync, readFileSync } from 'node:fs';
import path from 'node:path';
import { CONFIG_DIR, TOOLS_DIR } from './repo-paths.mjs';
import { readJson } from './json-utils.mjs';
import { auditLog } from './logger.mjs';
import {
    getRuntimeKey, getRuntimeModelSettings, isValidOpenRouterModelId,
} from './runtime-keys.mjs';

let cachedConfig = null;

export function llmConfig() {
    if (!cachedConfig) cachedConfig = readJson(path.join(CONFIG_DIR, 'llm.json'));
    return cachedConfig;
}

const IMAGE_MODEL_ROLES = ['imageCost', 'imageSpeed', 'imageAdult'];
const IMAGE_ROLE_LABELS = {
    imageCost: 'コスト重視',
    imageSpeed: '速度重視',
    imageAdult: 'アダルト緩和重視',
};

/** config/llm.json から「デフォルトに戻す」用の5モデルを一意に決める。
 *  画像3枠は catalog[].role（データ側の割り当て）で決める。image.model はあくまで
 *  「保存設定が無いときに実際に使うモデル」なので、枠の中身を image.model から導くと
 *  既定モデルを変えたときに枠の意味（速度重視 等）が入れ替わってしまう（2026-08-01）。 */
function defaultOpenRouterModelSettings() {
    const cfg = llmConfig();
    const image = cfg.image ?? {};
    const catalog = Array.isArray(image.catalog) ? image.catalog : [];
    const byRole = (role, positional, fallback) => catalog.find((entry) => entry?.role === role)?.id
        ?? catalog[positional]?.id ?? fallback;
    const defaultRole = IMAGE_MODEL_ROLES.includes(image.defaultRole) ? image.defaultRole : 'imageCost';
    return {
        analysis: cfg.tasks?.extract?.model ?? 'deepseek/deepseek-v4-flash-0731',
        json: cfg.tasks?.generateJson?.model ?? 'deepseek/deepseek-v4-flash-0731',
        imageCost: byRole('imageCost', 0, 'openai/gpt-image-2'),
        imageSpeed: byRole('imageSpeed', 1, 'black-forest-labs/flux.2-klein-4b'),
        imageAdult: image.nsfw?.recommendedModel ?? byRole('imageAdult', 2, 'x-ai/grok-imagine-image-quality'),
        imageActiveRole: defaultRole,
    };
}

/** API設定画面と実行処理が共有する、既定値／保存値／実効値。 */
export function openRouterModelSettings() {
    const cfg = llmConfig();
    const configuredImageModels = Array.isArray(cfg.image?.catalog) ? cfg.image.catalog : [];
    const defaults = defaultOpenRouterModelSettings();
    const overrides = getRuntimeModelSettings();
    const effective = { ...defaults, ...overrides };
    if (!IMAGE_MODEL_ROLES.includes(effective.imageActiveRole)) effective.imageActiveRole = defaults.imageActiveRole;
    const imageRoles = IMAGE_MODEL_ROLES.map((id) => ({ id, label: IMAGE_ROLE_LABELS[id] }));
    const imageModels = imageRoles.map(({ id: role, label: roleLabel }) => {
        const model = effective[role];
        const known = configuredImageModels.find((entry) => entry?.id === model) ?? {};
        return { ...known, id: model, role, roleLabel };
    });
    return {
        defaults,
        overrides,
        effective,
        activeImageModel: effective[effective.imageActiveRole],
        imageRoles,
        imageModels,
    };
}

/** 保存された3候補を、キャラ生成のモデル選択リスト用に整形する。 */
export function effectiveImageModelCatalog() {
    return openRouterModelSettings().imageModels;
}

function runtimeTextModel(task) {
    const settings = getRuntimeModelSettings();
    if (['extract', 'reduce', 'imagePrompt', 'worldDesign'].includes(task)) return settings.analysis ?? null;
    if (['generateJson', 'translate'].includes(task)) return settings.json ?? null;
    return null;
}

// ── OpenRouter キー解決（利用者が自分で発行したキーのみ。提供元のデフォルトキーは存在しない） ──

/**
 * OpenRouter API キーを解決する（値はログに出さない）。
 * 優先順: ①ランタイムストア（ブラウザ localStorage → リクエストヘッダで都度受領）
 *        ②環境変数（CLI 実行時にその場で渡す入力手段。保存はされない）
 * ファイルからは読まない（機密のファイル保存は禁止・2026-07-07 計画書§16.4）。
 */
export function resolveOpenRouterKey() {
    const runtime = getRuntimeKey('openrouter');
    if (runtime) return { key: runtime, source: 'ブラウザ（API設定画面・localStorage）' };
    const or = llmConfig().openrouter ?? {};
    const fromEnv = or.keyEnv ? process.env[or.keyEnv] : null;
    if (fromEnv && fromEnv.trim() !== '') return { key: fromEnv.trim(), source: `環境変数 ${or.keyEnv}（CLI 用・都度指定）` };
    return { key: null, source: null };
}

export function keySetupHint() {
    return 'OpenRouter API キーが未設定です。ツールの「API設定」画面から、ご自身で発行したキー（https://openrouter.ai/settings/keys）を登録してください（キーはお使いのブラウザにのみ保存されます）。';
}

function openRouterBase() {
    return String(llmConfig().openrouter?.baseUrl ?? 'https://openrouter.ai/api/v1').replace(/\/+$/, '');
}

export class LlmError extends Error {
    constructor(message, { code = 'llm_error', retryable = false, resetsAt = null, detail = null } = {}) {
        super(message);
        this.code = code;
        this.retryable = retryable;
        this.resetsAt = resetsAt;
        this.detail = detail;
    }
}

/** OpenRouter の疎通・残高確認（API設定画面と `llm status` 用。キー値は返さない）。 */
export async function llmHealth({ key: keyOverride = null } = {}) {
    const override = typeof keyOverride === 'string' && keyOverride.trim() !== '' ? keyOverride.trim() : null;
    const { key, source } = override
        ? { key: override, source: '入力中のキー（未保存）' }
        : resolveOpenRouterKey();
    const baseUrl = openRouterBase();
    if (!key) return { reachable: false, service: 'openrouter', baseUrl, keyConfigured: false, error: keySetupHint() };
    try {
        const res = await fetch(`${baseUrl}/auth/key`, {
            headers: { authorization: `Bearer ${key}` },
            signal: AbortSignal.timeout(10000),
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) {
            return {
                reachable: false, service: 'openrouter', baseUrl, keyConfigured: true, keySource: source,
                error: `キーが無効です (HTTP ${res.status})。「API設定」画面で正しいキーを登録し直してください`,
            };
        }
        const d = data?.data ?? {};
        return {
            reachable: true, service: 'openrouter', baseUrl, keyConfigured: true, keySource: source,
            usageUsd: d.usage ?? null, limitUsd: d.limit ?? null, remainingUsd: d.limit_remaining ?? null,
        };
    } catch (e) {
        return { reachable: false, service: 'openrouter', baseUrl, keyConfigured: true, keySource: source, error: e.message };
    }
}

/**
 * OpenRouter 公式の単一モデルAPIから単価を取得する。
 * pricing の値は公式APIが返す文字列をそのまま保持し、丸め・数値化しない。
 */
export async function openRouterModelPricing(model, { key: keyOverride = null } = {}) {
    const id = typeof model === 'string' ? model.trim() : '';
    if (!isValidOpenRouterModelId(id)) {
        throw new LlmError('モデルIDが不正です（例: deepseek/deepseek-v4-flash-0731）', { code: 'invalid_model_id' });
    }
    const [author, slug] = id.split('/');
    const override = typeof keyOverride === 'string' && keyOverride.trim() !== '' ? keyOverride.trim() : null;
    const { key } = override ? { key: override } : resolveOpenRouterKey();
    const headers = key ? { authorization: `Bearer ${key}` } : {};
    let res;
    try {
        res = await fetch(`${openRouterBase()}/model/${encodeURIComponent(author)}/${encodeURIComponent(slug)}`, {
            headers,
            signal: AbortSignal.timeout(15000),
        });
    } catch (e) {
        throw new LlmError(`OpenRouterからモデル単価を取得できません: ${e.message}`, { code: 'model_pricing_network' });
    }
    const data = await res.json().catch(() => null);
    if (!res.ok || !data?.data) {
        const detail = data?.error?.message ?? data?.error ?? '';
        const suffix = detail ? `: ${detail}` : '';
        throw new LlmError(
            res.status === 404
                ? `OpenRouterにモデルが存在しません: ${id}`
                : `OpenRouterのモデル単価取得に失敗しました（HTTP ${res.status}）${suffix}`,
            { code: 'model_pricing_failed' },
        );
    }
    const pricing = {};
    for (const [name, value] of Object.entries(data.data.pricing ?? {})) {
        if (typeof value === 'string' || typeof value === 'number') pricing[name] = String(value);
    }
    return {
        id: String(data.data.id ?? id),
        name: String(data.data.name ?? data.data.id ?? id),
        pricing,
    };
}

/**
 * そのタスクを実行するときに実際に使われるモデルID。
 * callLlmOnce と同じ優先順（UI設定 > 環境変数 > config）で解決するため、
 * コスト予測と実行が別モデルを指すことがない。
 */
export function resolveTaskModel(task) {
    const cfg = llmConfig();
    const modelOverride = process.env[cfg.modelOverrideEnv ?? 'LLM_MODEL_OVERRIDE']?.trim();
    return runtimeTextModel(task) ?? (modelOverride || null) ?? cfg.tasks?.[task]?.model ?? null;
}

/** ツール側から見た LLM 設定状態のサマリ（キー値は含めない）。 */
export function llmStatus() {
    const cfg = llmConfig();
    const { key, source } = resolveOpenRouterKey();
    return {
        service: 'openrouter（直接・利用者のキー）',
        baseUrl: openRouterBase(),
        tokenConfigured: !!key,
        tokenSource: source,
        tasks: Object.fromEntries(
            Object.entries(cfg.tasks ?? {})
                .filter(([k]) => !k.startsWith('_'))
                .map(([k, v]) => [k, {
                    provider: v.provider,
                    model: runtimeTextModel(k) ?? v.model,
                    providerRouting: v.providerRouting ?? cfg.providerRouting ?? null,
                }]),
        ),
        setupHint: key ? null : keySetupHint(),
    };
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ── 実費カウンタ（機能単位の合計ログ用。計画書§16.10）─────────────

/** プロセス内の累積カウンタ。server.mjs の createJob がジョブ前後の差分で「機能単位の合計」を出す。
 *  注意: ジョブを並列実行すると合計が混ざる（ローカル単独ユーザー前提。1 呼び出しごとの行は常に正確）。 */
const costCounter = { usd: 0, neurons: 0, calls: 0, extUsd: 0 }; // neurons は Workers AI 廃止に伴い常に 0（互換のため残置）

export function getWorkersAiCostSnapshot() {
    return { ...costCounter };
}

const fmtUsd = (usd) => {
    const minDigits = usd < 0.001 ? 6 : 4;
    let value = Number(usd).toFixed(10);
    while (value.endsWith('0') && value.split('.')[1].length > minDigits) value = value.slice(0, -1);
    return `$${value}`;
};
const usageCostUsd = (value) => value === null || value === undefined || value === ''
    ? Number.NaN
    : Number(value);
const fmtElapsed = (elapsedMs) => (Number.isFinite(elapsedMs) ? `・${(elapsedMs / 1000).toFixed(1)}秒` : '');

/** 機能単位の実費合計行（createJob / CLI ループの末尾用）。差分が 0 なら null。 */
export function formatWorkersAiCostDiff(before, { elapsedSec = null } = {}, after = getWorkersAiCostSnapshot()) {
    const usd = (after.extUsd - (before.extUsd ?? 0)) + (after.usd - (before.usd ?? 0));
    const calls = after.calls - before.calls;
    if (calls <= 0 || usd <= 0) return null;
    const time = elapsedSec !== null ? `・処理時間 ${elapsedSec.toFixed(1)}秒` : '';
    return `━ 実費合計: ${fmtUsd(usd)}（OpenRouter・${calls} 呼び出し${time}）`;
}

function logActualCost(onLog, costUsd, detail) {
    if (Number.isFinite(costUsd)) {
        costCounter.extUsd += costUsd;
        costCounter.calls += 1;
        onLog(`  💰 実費: ${fmtUsd(costUsd)}（OpenRouter・${detail}）`);
    } else {
        costCounter.calls += 1;
        onLog(`  💰 実費: 不明（OpenRouter が usage.cost を返しませんでした・${detail}）`);
    }
}

/** OpenRouter のエラー応答を LlmError へ変換（ユーザーへのダイアログ表示前提の文言）。 */
function toLlmError(status, err) {
    const raw = err?.message ?? `HTTP ${status}`;
    if (status === 401) {
        return new LlmError(
            `OpenRouter の API キーが無効です（HTTP 401）。「API設定」画面でご自身のキーを登録し直してください。\n      OpenRouter からの応答: ${String(raw).slice(0, 200)}`,
            { code: 'openrouter_auth' },
        );
    }
    if (status === 403) {
        // 403 はキー無効ではなく、入力がモデレーション（規制）でフラグされた拒否に使われる。
        // キー再登録へ誘導すると正しい対処（モデル切替 / プロンプト自動修正）に辿り着けない
        return new LlmError(
            `プロンプトが提供元の規制（モデレーション）に接触した可能性があります（HTTP 403）。\n      画像生成なら「grok へ切替」または「プロンプト自動修正」を選んで再実行してください。\n      OpenRouter からの応答: ${String(raw).slice(0, 200)}`,
            { code: 'content_flagged' },
        );
    }
    if (status === 402) {
        return new LlmError(
            `OpenRouter のクレジットが不足しています（HTTP 402）。https://openrouter.ai/credits で残高を追加してください。\n      OpenRouter からの応答: ${String(raw).slice(0, 200)}`,
            { code: 'openrouter_credits' },
        );
    }
    if (status === 429) {
        return new LlmError(`OpenRouter が一時的なレート制限を返しました: ${String(raw).slice(0, 200)}`, {
            code: 'rate_limited', retryable: true,
        });
    }
    return new LlmError(`[openrouter_error] ${String(raw).slice(0, 300)}`, {
        code: 'openrouter_error', retryable: status >= 500, detail: err ?? null,
    });
}

function truncatedResponseError({ generationId = null, partialChars = 0, cause = null } = {}) {
    const timedOut = cause?.name === 'TimeoutError' || /timed?\s*out|timeout/i.test(String(cause?.message ?? ''));
    return new LlmError(
        timedOut
            ? `LLM 応答本文の受信がタイムアウトしました。OpenRouter 側の混雑または提供モデルの遅延が考えられます。`
            : partialChars > 0
            ? `LLM の応答が完了前に途切れました（${partialChars.toLocaleString('en-US')}文字まで受信）。受信済み内容から自動復旧できない場合は、時間を置いて再実行してください。`
            : 'LLM の応答を受信できませんでした。接続が生成開始前に途切れた可能性があります。時間を置いて再実行してください。',
        {
            code: timedOut ? 'request_timeout' : 'response_truncated',
            // 本文を1文字も受信できなかった一時障害は安全に自動回復を試す。
            // 受信途中の通常テキストは二重課金と内容重複を避けるため再試行しない
            //（JSON/SSEは allowPartialContent で先に構文復旧へ渡される）。
            retryable: partialChars === 0,
            detail: { generationId, partialChars, cause: cause ? String(cause.message ?? cause).slice(0, 300) : null },
        },
    );
}

function streamError(error, partialChars) {
    const raw = String(error?.message ?? 'OpenRouter のストリーミング応答でエラーが発生しました').slice(0, 300);
    const type = String(error?.metadata?.error_type ?? error?.error_type ?? '').toLowerCase();
    const status = Number(error?.code ?? 0);
    if (type.includes('content_policy') || status === 403) {
        return new LlmError(`モデルが生成内容を拒否しました: ${raw}`, { code: 'content_flagged', detail: error });
    }
    if (type.includes('max_tokens') || type.includes('context_length')) {
        return new LlmError(`モデルの出力上限に達しました: ${raw}`, { code: 'output_limit', detail: error });
    }
    if (type.includes('rate_limit') || status === 429) {
        return new LlmError(`OpenRouter がレート制限を返しました: ${raw}`, {
            code: 'rate_limited', retryable: partialChars === 0, detail: error,
        });
    }
    const retryable = partialChars === 0 && (status >= 500 || /timeout|overload|unavailable|disconnect/.test(type));
    return new LlmError(`OpenRouter の生成が途中で終了しました: ${raw}`, {
        code: type ? `stream_${type}` : 'stream_error', retryable, detail: error,
    });
}

/**
 * OpenRouter Chat Completions の SSE をチャンク境界に依存せず読み取る。
 * keep-alive コメントは無視し、最終 usage、finish_reason、途中エラーを保持する。
 * JSON 呼び出しでは allowPartialContent=true とし、終端だけ欠けた場合は受信済みJSONを
 * callLlmJson 側で検証・復旧できるよう返す（不完全なら既存のJSON再出力処理へ進む）。
 */
export async function readOpenRouterSse(response, { allowPartialContent = false } = {}) {
    const generationIdHeader = response.headers?.get?.('x-generation-id') ?? null;
    if (!response.body?.getReader) throw truncatedResponseError({ generationId: generationIdHeader });

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let dataLines = [];
    let text = '';
    let usage = null;
    let model = null;
    let responseId = generationIdHeader;
    let finishReason = null;
    let done = false;
    let eventCount = 0;

    const result = (incomplete = false, cause = null) => ({
        text, usage, model, generationId: responseId, finishReason,
        incomplete, done, eventCount,
        ...(cause ? { transportError: String(cause.message ?? cause).slice(0, 300) } : {}),
    });
    const handleEvent = () => {
        if (dataLines.length === 0) return;
        const payload = dataLines.join('\n');
        dataLines = [];
        if (payload === '[DONE]') {
            done = true;
            return;
        }
        let chunk;
        try {
            chunk = JSON.parse(payload);
        } catch (e) {
            throw new LlmError('OpenRouter のストリーミング応答に解釈できないデータが含まれていました。', {
                code: 'invalid_stream_event', detail: { payload: payload.slice(0, 300), cause: e.message },
            });
        }
        eventCount += 1;
        responseId = chunk.id ?? responseId;
        model = chunk.model ?? model;
        if (chunk.usage) usage = chunk.usage;
        const choice = chunk.choices?.[0];
        const content = choice?.delta?.content ?? choice?.message?.content;
        if (typeof content === 'string') text += content;
        if (choice?.finish_reason !== undefined && choice.finish_reason !== null) finishReason = choice.finish_reason;
        const error = chunk.error ?? choice?.error;
        if (error || finishReason === 'error') throw streamError(error, text.length);
    };
    const handleLine = (rawLine) => {
        const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
        if (line === '') {
            handleEvent();
            return;
        }
        if (line.startsWith(':')) return; // `: OPENROUTER PROCESSING` 等の keep-alive
        if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
    };
    const consume = (chunkText) => {
        buffer += chunkText;
        for (;;) {
            const newline = buffer.indexOf('\n');
            if (newline < 0) break;
            const line = buffer.slice(0, newline);
            buffer = buffer.slice(newline + 1);
            handleLine(line);
        }
    };

    try {
        for (;;) {
            const { value, done: readerDone } = await reader.read();
            if (readerDone) break;
            consume(decoder.decode(value, { stream: true }));
        }
        consume(decoder.decode());
        if (buffer !== '') handleLine(buffer);
        handleEvent();
    } catch (e) {
        if (e instanceof LlmError) throw e;
        if (allowPartialContent && text.trim() !== '') return result(true, e);
        throw truncatedResponseError({ generationId: responseId, partialChars: text.length, cause: e });
    } finally {
        reader.releaseLock?.();
    }

    // [DONE] が無くても finish_reason まで受信済みなら、末尾の接続終了として安全に扱う。
    if (!done && finishReason === null) {
        if (allowPartialContent && text.trim() !== '') return result(true);
        throw truncatedResponseError({ generationId: responseId, partialChars: text.length });
    }
    return result(false);
}

// ── テキスト生成（chat completions 直接） ─────────────────────────

/**
 * OpenRouter chat completions を 1 タスク分呼び出す（llm-hub 経由は廃止・直接）。
 * config の `tasks.<task>.fallback: {provider:'openrouter', model}` があれば失敗時 1 回だけ切替（OpenRouter モデル間のみ）。
 * @param {string} task config/llm.json の tasks キー
 * @param {object} opts { prompt, system, messages, maxTokens, temperature, responseFormat, provider, model, onLog }
 * @returns {Promise<{text, usage, model, provider, elapsedMs, refused, costUsd}>}
 */
export async function callLlm(task, opts = {}) {
    const cfg = llmConfig();
    const taskCfg = cfg.tasks?.[task] ?? {};
    try {
        return await callLlmOnce(task, opts);
    } catch (e) {
        const fb = taskCfg.fallback;
        const overridden = Boolean(
            opts.provider || opts.model ||
            runtimeTextModel(task) ||
            process.env[cfg.modelOverrideEnv ?? 'LLM_MODEL_OVERRIDE']?.trim() ||
            process.env[cfg.providerOverrideEnv ?? 'LLM_PROVIDER_OVERRIDE']?.trim(),
        );
        const NO_FALLBACK = ['token_missing', 'openrouter_auth', 'openrouter_credits', 'provider_forbidden'];
        if (
            e instanceof LlmError && !NO_FALLBACK.includes(e.code) && !overridden &&
            fb?.model && (fb.provider ?? 'openrouter') === 'openrouter'
        ) {
            const onLog = opts.onLog ?? (() => {});
            onLog(`  ⚠ ${task} の主モデルが失敗（${e.code}）。フォールバック openrouter（${fb.model}）へ切り替えます`);
            return await callLlmOnce(task, {
                ...opts, provider: 'openrouter', model: fb.model,
                ...(fb.maxTokens !== undefined ? { maxTokens: fb.maxTokens } : {}),
            });
        }
        throw e;
    }
}

/** provider が openrouter 以外なら明示エラー（Workers AI 全面禁止・2026-07-07）。 */
function requireOpenRouterProvider(provider) {
    if ((provider ?? 'openrouter') !== 'openrouter') {
        throw new LlmError(
            `provider "${provider}" は本ツールでは使用禁止です（Workers AI 等はフォールバック含め全面禁止・2026-07-07）。すべて OpenRouter（「API設定」画面のキー）で実行されます。`,
            { code: 'provider_forbidden' },
        );
    }
}

function textRetryDelayMs(retryNumber, retryDelaysMs, retryBaseMs) {
    const configured = Number(Array.isArray(retryDelaysMs) ? retryDelaysMs[retryNumber - 1] : Number.NaN);
    if (Number.isFinite(configured) && configured >= 0) return configured;
    const base = Number.isFinite(Number(retryBaseMs)) && Number(retryBaseMs) >= 0 ? Number(retryBaseMs) : 10000;
    return base * retryNumber;
}

async function waitForTextRetry({ task, attempt, maxRetries, error, onLog, retryDelaysMs, retryBaseMs }) {
    const retryNumber = attempt + 1;
    const waitMs = textRetryDelayMs(retryNumber, retryDelaysMs, retryBaseMs);
    const waitSec = waitMs / 1000;
    onLog(`  ⚠ ${error?.code ?? 'network_error'}: ${String(error?.message ?? '一時的な通信障害').slice(0, 160)}`);
    onLog(`  ⏳ ${Number.isInteger(waitSec) ? waitSec : waitSec.toFixed(1)}秒待機後にリトライ ${retryNumber}/${maxRetries} を実行します`);
    auditLog('llm-retry', {
        task, code: error?.code ?? 'network_error', retryNumber, maxRetries, waitMs,
    });
    await sleep(waitMs);
}

async function callLlmOnce(task, opts = {}) {
    const cfg = llmConfig();
    const taskCfg = cfg.tasks?.[task] ?? {};
    const { key } = resolveOpenRouterKey();
    if (!key) throw new LlmError(keySetupHint(), { code: 'token_missing' });
    const onLog = opts.onLog ?? (() => {});

    const modelOverride = process.env[cfg.modelOverrideEnv ?? 'LLM_MODEL_OVERRIDE']?.trim();
    const providerOverride = process.env[cfg.providerOverrideEnv ?? 'LLM_PROVIDER_OVERRIDE']?.trim();
    requireOpenRouterProvider(opts.provider ?? (providerOverride || taskCfg.provider) ?? 'openrouter');
    // UIの保存設定はサーバ環境変数より優先。環境変数はUI設定が無いCLI実行用。
    const model = opts.model ?? runtimeTextModel(task) ?? modelOverride ?? taskCfg.model;
    if (!model) throw new LlmError(`タスク ${task} のモデルが未設定です（config/llm.json）`, { code: 'model_missing' });

    const body = {
        model,
        max_tokens: opts.maxTokens ?? taskCfg.maxTokens ?? 1024,
        usage: { include: true }, // 実費 $ を応答で受け取る
    };
    const streaming = opts.stream ?? taskCfg.stream ?? false;
    if (streaming) {
        body.stream = true;
        body.stream_options = { include_usage: true };
    }
    const temperature = opts.temperature ?? taskCfg.temperature;
    if (temperature !== undefined && temperature !== null) body.temperature = temperature;
    // 推論系モデル（DeepSeek V4 等）の思考トークン制御（OpenRouter 統一パラメータ）。
    // 本ツールの全タスクは構造化 JSON 出力のため、config/llm.json 既定で reasoning を無効化し、
    // 「思考で max_tokens を使い切って content が空になる（empty_completion）」事故と
    // 応答遅延・課金増を防ぐ（タスク別・呼び出し別に上書き可）
    const reasoning = opts.reasoning ?? taskCfg.reasoning ?? cfg.reasoning;
    if (reasoning !== undefined) body.reasoning = reasoning;
    // 提供元（プロバイダ）の指定（OpenRouter のルーティング設定をそのまま body.provider へ渡す）。
    // 同じモデル ID でも提供元ごとに単価・出力上限・対応パラメータが変わるため、config/llm.json の
    // providerRouting で固定する（2026-08-01: DeepInfra 固定＝$0.09/$0.18・出力上限 65,536tok）
    const providerRouting = opts.providerRouting ?? taskCfg.providerRouting ?? cfg.providerRouting;
    if (providerRouting && typeof providerRouting === 'object') body.provider = providerRouting;
    const responseFormat = opts.responseFormat ?? taskCfg.responseFormat;
    if (responseFormat) body.response_format = responseFormat;
    body.messages = Array.isArray(opts.messages) && opts.messages.length > 0
        ? opts.messages
        : [
            ...(opts.system ? [{ role: 'system', content: opts.system }] : []),
            { role: 'user', content: opts.prompt ?? '' },
        ];

    const url = `${openRouterBase()}/chat/completions`;
    const configuredMaxRetries = Number(opts.maxRetries ?? taskCfg.maxRetries ?? cfg.maxRetries ?? 3);
    const maxRetries = Number.isFinite(configuredMaxRetries) ? Math.max(0, Math.trunc(configuredMaxRetries)) : 3;
    const retryDelaysMs = opts.retryDelaysMs ?? taskCfg.retryDelaysMs ?? cfg.retryDelaysMs;
    const retryBaseMs = opts.retryBaseMs ?? taskCfg.retryBaseMs ?? cfg.retryBaseMs ?? 10000;
    const configuredTimeoutMs = Number(opts.requestTimeoutMs ?? taskCfg.requestTimeoutMs ?? cfg.requestTimeoutMs ?? 300000);
    const requestTimeoutMs = Number.isFinite(configuredTimeoutMs) && configuredTimeoutMs > 0
        ? configuredTimeoutMs : 300000;
    let lastError = null;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        if (attempt > 0) onLog(`  ↻ リトライ ${attempt}/${maxRetries} を開始します`);
        const started = Date.now();
        try {
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'content-type': 'application/json', authorization: `Bearer ${key}` },
                body: JSON.stringify(body),
                signal: AbortSignal.timeout(requestTimeoutMs),
            });
            let streamResult = null;
            let data = null;
            let responseReadError = null;
            const responseContentType = String(res.headers?.get?.('content-type') ?? '').toLowerCase();
            if (res.ok && streaming && responseContentType.includes('text/event-stream')) {
                streamResult = await readOpenRouterSse(res, { allowPartialContent: opts.allowPartialStream === true });
                data = {
                    id: streamResult.generationId,
                    model: streamResult.model ?? model,
                    choices: [{
                        message: { content: streamResult.text },
                        finish_reason: streamResult.finishReason,
                    }],
                    usage: streamResult.usage ?? {},
                };
                if (streamResult.incomplete) {
                    onLog(`  ⚠ LLM 応答の終端を受信できなかったため、受信済み ${streamResult.text.length.toLocaleString('en-US')}文字から自動復旧します`);
                }
            } else {
                try {
                    data = await res.json();
                } catch (error) {
                    // fetch() はヘッダ受信時点で解決するため、本文読取中の300秒タイムアウトは
                    // ここで初めて発生する。原因を保持しないと単なる「本文なし」になり、
                    // request_timeout のリトライ経路へ入れない。
                    responseReadError = error;
                }
            }
            const elapsedMs = Date.now() - started;
            if (res.ok && data === null) {
                // 200 だが本文の受信/JSON 化に失敗（転送中の切断等）。「HTTP 200 のエラー」という
                // 自己矛盾した表示にせず、本文が0文字なら一時障害として段階的リトライへ渡す。
                throw truncatedResponseError({
                    generationId: res.headers?.get?.('x-generation-id') ?? null,
                    cause: responseReadError,
                });
            }
            if (res.ok && data && Array.isArray(data.choices)) {
                const choice = data.choices[0] ?? {};
                const content = choice.message?.content;
                const text = typeof content === 'string' ? content : '';
                const u = data.usage ?? {};
                const usage = { inputTokens: Number(u.prompt_tokens ?? 0), outputTokens: Number(u.completion_tokens ?? 0) };
                const costUsd = usageCostUsd(u.cost);
                // content 空・choice 埋め込みエラー・content_filter は「成功」にしない
                //（そのまま返すと JSON 修復リトライがもう 1 回課金してほぼ確実に再失敗する）
                if (data.error || choice.error || choice.finish_reason === 'error' || choice.finish_reason === 'content_filter' || text.trim() === '') {
                    const flagged = choice.finish_reason === 'content_filter';
                    const reason = data.error?.message ?? choice.error?.message ?? (flagged
                        ? 'モデルが生成を拒否しました（content_filter）'
                        : choice.finish_reason === 'length'
                            ? '出力トークン上限に達しましたが本文が空でした（推論系モデルが思考でトークンを使い切った可能性）'
                            : 'モデルの応答が空でした');
                    // 有効な本文がなくても OpenRouter が usage.cost を返した呼び出しは課金済み。
                    // 後続リトライを含む工程合計から漏らさず、金額なしの場合も「不明」として残す。
                    logActualCost(onLog, costUsd,
                        `失敗応答・入 ${usage.inputTokens.toLocaleString('en-US')} / 出 ${usage.outputTokens.toLocaleString('en-US')}tok${fmtElapsed(elapsedMs)}`);
                    auditLog('llm-call', {
                        task, provider: 'openrouter', model: data.model ?? model, ok: false,
                        code: flagged ? 'content_flagged' : 'empty_completion',
                        finishReason: choice.finish_reason ?? null, elapsedMs, usage,
                        costUsd: Number.isFinite(costUsd) ? costUsd : null, attempt,
                    });
                    // content_filter は決定的（リトライ無意味）。空応答はモデル側の一時的問題が
                    // ほとんどのため retryable にして既存のリトライ機構（maxRetries）へ乗せる
                    throw new LlmError(
                        `LLM が有効な応答を返しませんでした: ${String(reason).slice(0, 200)}。${flagged
                            ? 'プロンプトの内容（規制接触）の可能性があります。'
                            : 'モデル側の一時的な問題の可能性があります。'}`,
                        { code: flagged ? 'content_flagged' : 'empty_completion', retryable: !flagged },
                    );
                }
                auditLog('llm-call', {
                    task, provider: 'openrouter', model: data.model ?? model, ok: true,
                    elapsedMs, usage, costUsd: Number.isFinite(costUsd) ? costUsd : null, attempt,
                    streamRequested: streaming, streamed: streamResult !== null,
                    streamIncomplete: streamResult?.incomplete === true,
                    generationId: data.id ?? null,
                });
                logActualCost(onLog, costUsd,
                    `入 ${usage.inputTokens.toLocaleString('en-US')} / 出 ${usage.outputTokens.toLocaleString('en-US')}tok${fmtElapsed(elapsedMs)}`);
                return {
                    text, usage, model: data.model ?? model, provider: 'openrouter', elapsedMs,
                    refused: false, costUsd: Number.isFinite(costUsd) ? costUsd : null,
                    streamRequested: streaming, streamed: streamResult !== null,
                    streamIncomplete: streamResult?.incomplete === true,
                    generationId: data.id ?? null,
                };
            }
            lastError = toLlmError(res.status, data?.error);
            if (!lastError.retryable || attempt === maxRetries) throw lastError;
            await waitForTextRetry({
                task, attempt, maxRetries, error: lastError, onLog, retryDelaysMs, retryBaseMs,
            });
        } catch (e) {
            if (e instanceof LlmError) {
                if (['request_timeout', 'response_truncated'].includes(e.code)) {
                    logActualCost(onLog, Number.NaN, `${e.code === 'request_timeout' ? '応答タイムアウト' : '応答本文を取得できず'}金額不明`);
                }
                if (!e.retryable || attempt === maxRetries) {
                    auditLog('llm-call', { task, ok: false, code: e.code, attempt });
                    throw e;
                }
                lastError = e;
                await waitForTextRetry({
                    task, attempt, maxRetries, error: lastError, onLog, retryDelaysMs, retryBaseMs,
                });
                continue;
            }
            if (e?.name === 'TimeoutError') {
                // ヘッダ受信前のタイムアウト。サーバ側で課金済みの可能性はログへ明示しつつ、
                // ユーザー指定の段階待機で一時的な混雑から自動回復する。
                logActualCost(onLog, Number.NaN, '応答タイムアウトのため金額不明');
                lastError = new LlmError(
                    `LLM 応答がタイムアウトしました（${Math.round(requestTimeoutMs / 1000)}秒）。OpenRouter 側の混雑または提供モデルの遅延が考えられます。`,
                    { code: 'request_timeout', retryable: true },
                );
                if (attempt === maxRetries) {
                    auditLog('llm-call', { task, ok: false, code: 'request_timeout', attempt });
                    throw lastError;
                }
                await waitForTextRetry({
                    task, attempt, maxRetries, error: lastError, onLog, retryDelaysMs, retryBaseMs,
                });
                continue;
            }
            lastError = new LlmError(`OpenRouter へ接続できません: ${e.message}`, { code: 'network_error', retryable: true });
            if (attempt === maxRetries) {
                auditLog('llm-call', { task, ok: false, code: 'network_error', attempt });
                throw lastError;
            }
            await waitForTextRetry({
                task, attempt, maxRetries, error: lastError, onLog, retryDelaysMs, retryBaseMs,
            });
        }
    }
    throw lastError ?? new LlmError('LLM 呼び出しに失敗しました', { code: 'unknown' });
}

// ── 画像生成（/api/v1/images 直接・計画書§16.10） ─────────────────

/** 先頭バイトのマジックナンバーから contentType を推定（png/jpeg/webp。既定 png）。 */
function sniffContentType(buf) {
    if (buf.length >= 4 && buf[0] === 0x89 && buf[1] === 0x50 && buf[2] === 0x4e && buf[3] === 0x47) return 'image/png';
    if (buf.length >= 3 && buf[0] === 0xff && buf[1] === 0xd8 && buf[2] === 0xff) return 'image/jpeg';
    if (buf.length >= 12 && buf[0] === 0x52 && buf[1] === 0x49 && buf[8] === 0x57 && buf[9] === 0x45) return 'image/webp';
    return 'image/png';
}

/**
 * 画像を 1 枚生成する（OpenRouter /api/v1/images 直接）。
 * @param {object} opts { prompt, provider?, model?, params?, onLog? }
 * @returns {Promise<{imageBase64, contentType, bytes, model, provider, usageUsd, elapsedMs}>}
 */
export async function callImage(opts = {}) {
    const img = llmConfig().image ?? {};
    const onLog = opts.onLog ?? (() => {});
    if (typeof opts.prompt !== 'string' || opts.prompt.trim() === '') {
        throw new LlmError('画像生成プロンプトが空です', { code: 'invalid_prompt' });
    }
    const spec = resolveImageCallSpec(img, opts);
    return await callImageOnce(img, spec, opts.prompt, onLog);
}

/**
 * provider/model/params を config と引数から解決する。Workers AI 等は禁止（openrouter のみ）。
 * 優先度: 引数 > API設定画面の保存値 > 環境変数 > config 主設定 > ハードコード既定。
 */
export function resolveImageCallSpec(img, opts = {}) {
    requireOpenRouterProvider((typeof opts.provider === 'string' && opts.provider.trim()) || img.provider || 'openrouter');
    const modelOverride = img.modelEnv ? process.env[img.modelEnv]?.trim() : null;
    const saved = getRuntimeModelSettings();
    const hasSavedImageSetting = IMAGE_MODEL_ROLES.some((role) => typeof saved[role] === 'string')
        || IMAGE_MODEL_ROLES.includes(saved.imageActiveRole);
    const savedModel = hasSavedImageSetting ? openRouterModelSettings().activeImageModel : null;
    const model = (typeof opts.model === 'string' && opts.model.trim()) || savedModel || modelOverride || img.model || 'openai/gpt-image-2';
    // パラメータはモデル単位（config の image.modelParams）で決める。既定モデルを差し替えても
    // 別モデルのパラメータが渡らないようにするため、共通既定 image.params は最後の保険だけに使う（2026-08-01）
    const params = opts.params ?? img.modelParams?.[model] ?? img.params ?? {};
    return { provider: 'openrouter', model, params };
}

async function callImageOnce(img, { model, params }, prompt, onLog) {
    const cfg = llmConfig();
    const { key } = resolveOpenRouterKey();
    if (!key) throw new LlmError(keySetupHint(), { code: 'token_missing' });

    const url = `${openRouterBase()}/images`;
    const maxRetries = img.maxRetries ?? cfg.maxRetries ?? 2;
    const retryBaseMs = img.retryBaseMs ?? cfg.retryBaseMs ?? 5000;
    let lastError = null;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        if (attempt > 0) onLog(`  リトライ ${attempt}/${maxRetries}（${lastError?.code ?? 'network'}: ${lastError?.message?.slice(0, 80)}）`);
        const started = Date.now();
        try {
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'content-type': 'application/json', authorization: `Bearer ${key}` },
                body: JSON.stringify({ model, prompt, ...params }),
                signal: AbortSignal.timeout(img.requestTimeoutMs ?? cfg.requestTimeoutMs ?? 300000),
            });
            const data = await res.json().catch(() => null);
            const elapsedMs = Date.now() - started;
            const b64 = data?.data?.[0]?.b64_json;
            if (res.ok && typeof b64 === 'string' && b64 !== '') {
                // Buffer はブラウザに存在しないため使わない（本モジュールは esbuild でブラウザ版にも
                // バンドルされる。atob は Node 24 / ブラウザ両方のグローバル）
                const buf = typeof Buffer !== 'undefined'
                    ? Buffer.from(b64, 'base64')
                    : Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
                const costUsd = usageCostUsd(data?.usage?.cost);
                auditLog('image-call', {
                    provider: 'openrouter', model, ok: true, elapsedMs,
                    bytes: buf.length, usageUsd: Number.isFinite(costUsd) ? costUsd : null, attempt,
                });
                logActualCost(onLog, costUsd, `画像 ${Math.round(buf.length / 1024)}KB${fmtElapsed(elapsedMs)}`);
                return {
                    imageBase64: b64,
                    contentType: sniffContentType(buf),
                    bytes: buf.length,
                    model,
                    provider: 'openrouter',
                    usageUsd: Number.isFinite(costUsd) ? costUsd : null,
                    elapsedMs,
                };
            }
            if (res.ok) {
                // 画像本体が欠落していても、usage.cost があればその応答は課金済みとして記録する。
                const costUsd = usageCostUsd(data?.usage?.cost);
                logActualCost(onLog, costUsd, `画像失敗応答${fmtElapsed(elapsedMs)}`);
            }
            lastError = res.ok
                ? new LlmError('OpenRouter 応答から画像データ（b64_json）を取り出せません', { code: 'image_generation_failed', detail: data })
                : toLlmError(res.status, data?.error);
            if (!lastError.retryable || attempt === maxRetries) throw lastError;
            const waitSec = Math.ceil((retryBaseMs * (attempt + 1)) / 1000);
            onLog(`  ${lastError.code}: ${waitSec}秒待って再試行します`);
            await sleep(waitSec * 1000);
        } catch (e) {
            if (e instanceof LlmError) {
                if (!e.retryable || attempt === maxRetries) {
                    auditLog('image-call', { model, ok: false, code: e.code, attempt });
                    throw e;
                }
                lastError = e;
                await sleep(retryBaseMs * (attempt + 1));
                continue;
            }
            if (e?.name === 'TimeoutError') {
                // 画像生成のタイムアウト後もサーバ側の生成・課金は止まらないため、
                // 自動リトライで同じ画像に二重課金しない（時間を置いた手動再実行を案内）
                logActualCost(onLog, Number.NaN, '画像応答タイムアウトのため金額不明');
                lastError = new LlmError(
                    '画像生成がタイムアウトしました。サーバ側で生成（課金）は継続している可能性があります。時間を置いて再実行してください。',
                    { code: 'request_timeout' },
                );
                auditLog('image-call', { model, ok: false, code: 'request_timeout', attempt });
                throw lastError;
            }
            lastError = new LlmError(`OpenRouter へ接続できません: ${e.message}`, { code: 'network_error', retryable: true });
            if (attempt === maxRetries) {
                auditLog('image-call', { model, ok: false, code: 'network_error', attempt });
                throw lastError;
            }
            await sleep(retryBaseMs * (attempt + 1));
        }
    }
    throw lastError ?? new LlmError('画像生成に失敗しました', { code: 'unknown' });
}

// ── JSON 呼び出し・ユーティリティ（従来どおり） ─────────────────────

/**
 * JSON 応答を期待する呼び出し。パース失敗時は 1 回だけ「JSON のみで再出力」を依頼して再試行する。
 * @returns {Promise<{json, raw, usage, model, provider, calls}>}
 */
export async function callLlmJson(task, opts = {}) {
    const onLog = opts.onLog ?? (() => {});
    // JSON は、SSE の終端だけ欠けても本文が完全ならそのまま検証・採用できる。
    // 本文が未完成なら既存の JSON 再出力へ進み、壊れた内容を保存しない。
    const jsonCallOpts = { ...opts, allowPartialStream: true };
    const first = await callLlm(task, jsonCallOpts);
    try {
        const json = parseJsonLoose(first.text, onLog);
        if (first.streamIncomplete) onLog('  ✓ 受信済みの JSON は完全だったため、そのまま復旧しました');
        return { json, raw: first.text, usage: first.usage, model: first.model, provider: first.provider, calls: 1 };
    } catch (parseError) {
        onLog(`  JSON 解釈に失敗（${parseError.message.slice(0, 100)}）。JSON のみでの再出力を依頼します`);
        const retryMessages = [
            ...(opts.system ? [{ role: 'system', content: opts.system }] : []),
            { role: 'user', content: opts.prompt ?? '' },
            { role: 'assistant', content: first.text.slice(0, 6000) },
            { role: 'user', content: '上の出力は JSON として解釈できませんでした。同じ内容を、コードフェンス・説明文・コメント無しの有効な JSON オブジェクト 1個だけで出力し直してください。' },
        ];
        const second = await callLlm(task, {
            ...jsonCallOpts, prompt: undefined, system: undefined, messages: retryMessages,
        });
        const usage = mergeUsage(first.usage, second.usage);
        const json = parseJsonLoose(second.text, onLog);
        if (second.streamIncomplete) onLog('  ✓ 再出力で受信済みの JSON は完全だったため、そのまま復旧しました');
        return { json, raw: second.text, usage, model: second.model, provider: second.provider, calls: 2 };
    }
}

export function mergeUsage(a, b) {
    if (!a && !b) return null;
    return {
        inputTokens: (a?.inputTokens ?? 0) + (b?.inputTokens ?? 0),
        outputTokens: (a?.outputTokens ?? 0) + (b?.outputTokens ?? 0),
    };
}

/** LLM 応答テキストから JSON を寛容に取り出す（コードフェンス・前置き文・末尾カンマ・行コメント耐性）。
 *  onLog を渡すと、自動修復（末尾カンマ除去等）でパースが通った場合に警告を 1 行流す
 *  （修復は文字列リテラル内も書き換え得るため、黙って通さず内容確認を促す）。 */
export function parseJsonLoose(text, onLog = null) {
    if (typeof text !== 'string' || text.trim() === '') throw new Error('LLM 応答が空です');
    let t = text.trim();
    const fence = t.match(/^```[^\n]*\n([\s\S]*?)\n?```\s*$/);
    if (fence) t = fence[1].trim();
    const firstBrace = t.indexOf('{');
    const firstBracket = t.indexOf('[');
    const candidates = [firstBrace, firstBracket].filter((i) => i >= 0);
    if (candidates.length === 0) throw new Error(`JSON が見つかりません: ${t.slice(0, 120)}`);
    const first = Math.min(...candidates);
    const close = t[first] === '{' ? '}' : ']';
    const last = t.lastIndexOf(close);
    if (last <= first) throw new Error(`JSON の閉じ括弧が見つかりません: ${t.slice(0, 120)}`);
    t = t.slice(first, last + 1);
    const attempts = [
        (s) => s,
        (s) => s.replace(/,\s*([}\]])/g, '$1'),
        (s) => s.replace(/^\s*\/\/[^\n]*$/gm, '').replace(/,\s*([}\]])/g, '$1'),
    ];
    let lastErr = null;
    for (const [i, fix] of attempts.entries()) {
        try {
            const parsed = JSON.parse(fix(t));
            if (i > 0 && typeof onLog === 'function') {
                onLog('  ⚠ JSON を自動修復して解釈しました（末尾カンマ/コメント除去。文中に同じ並びがあると本文が欠落し得るため、生成結果に欠けがないか確認してください）');
            }
            return parsed;
        } catch (e) {
            lastErr = e;
        }
    }
    throw new Error(`LLM 応答を JSON として解釈できません（${lastErr.message}）: ${t.slice(0, 200)}`);
}

/**
 * 長文を段落境界を優先してチャンク分割する。
 * maxChunks を超える場合はチャンク幅を自動拡大して収める。
 */
export function chunkText(text, { chars = 9000, overlapChars = 400, maxChunks = 60 } = {}) {
    const clean = String(text).replace(/\r\n/g, '\n');
    if (clean.length <= chars) return [clean];
    let size = chars;
    const estimated = Math.ceil(clean.length / Math.max(1, size - overlapChars));
    if (estimated > maxChunks) size = Math.ceil(clean.length / maxChunks) + overlapChars;
    const chunks = [];
    let start = 0;
    while (start < clean.length) {
        let end = Math.min(start + size, clean.length);
        if (end < clean.length) {
            const nl = clean.lastIndexOf('\n', end);
            if (nl > start + size * 0.5) end = nl;
        }
        chunks.push(clean.slice(start, end));
        if (end >= clean.length) break;
        start = Math.max(end - overlapChars, start + 1);
    }
    return chunks;
}

// ── 旧 llm-hub 互換（ベンチスクリプト用に残置。ツール本流では未使用） ──

export function resolveBaseUrl() {
    const legacy = llmConfig()._legacyLlmHub ?? {};
    const fromEnv = legacy.baseUrlEnv ? process.env[legacy.baseUrlEnv] : null;
    return String(fromEnv || legacy.baseUrl || '').replace(/\/+$/, '');
}

export function resolveToken() {
    const legacy = llmConfig()._legacyLlmHub ?? {};
    const fromEnv = legacy.tokenEnv ? process.env[legacy.tokenEnv] : null;
    if (fromEnv && fromEnv.trim() !== '') return { token: fromEnv.trim(), source: `環境変数 ${legacy.tokenEnv}` };
    const file = path.join(TOOLS_DIR, legacy.tokenFile ?? '.secrets/llm-hub-token');
    if (existsSync(file)) {
        const value = readFileSync(file, 'utf-8').trim();
        if (value !== '') return { token: value, source: legacy.tokenFile };
    }
    return { token: null, source: null };
}
